import { useEffect, useRef } from 'react'

import Chip from '@/components/ui/Chip'
import { useRovingTabs } from '@/components/ui/use-roving-tabs'
import { cn } from '@/lib/utils'

export interface ChipTabOption {
  value: string
  label: string
  /** Decorative status dot, e.g. the pulsing mark on "LIVE". */
  dot?: 'up' | 'down' | 'accent' | 'muted'
}

interface ChipTabsProps {
  options: readonly ChipTabOption[]
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  /**
   * `filter` (default) is the 28px chip row. `subtab` (sz:kit) is the stock page's Advanced
   * sub-tab strip (mock `ctl-vchip`, owner ruling C7): 36px boxed chips 8 apart at every width.
   */
  variant?: 'filter' | 'subtab'
  className?: string
}

/**
 * A tablist, not a radio group: the timeframe row and the Happening filters
 * both swap the panel beside them rather than submitting a value.
 *
 * Tabs use a roving tabindex — one Tab press reaches the group, then the arrow
 * keys (and Home/End) move within it; TabBar's hook, so the two tab controls
 * answer the same keys. Leaving all six chips in the tab order would cost a
 * keyboard user five presses to walk past a control they did not want.
 *
 * Below `lg` the row scrolls sideways instead of wrapping, and the gap opens
 * from 4px to 8px so two neighbouring chips are not one thumb wide together.
 * Wrapping comes back at `lg`, which is what the frames draw.
 */
export default function ChipTabs({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'filter',
  className,
}: ChipTabsProps) {
  const { onKeyDown, register } = useRovingTabs(options, value, onChange)
  const stripRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement | null>(null)

  // By hand rather than with scrollIntoView, which walks every scrollable
  // ancestor and would jog the page vertically as well as the strip.
  useEffect(() => {
    const strip = stripRef.current
    const tab = activeRef.current
    if (strip === null || tab === null) return
    if (strip.scrollWidth <= strip.clientWidth) return
    strip.scrollLeft = Math.max(0, tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2)
  }, [value])

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      // `py-2` holds all of Chip's 8px below-`lg` hit area inside the scroll box — with less, the
      // `::before` overflowed it and the strip scrolled vertically too — and `-my-2` gives the 8px
      // back, so the strip takes the chips' own height in the layout.
      className={cn(
        'scrollbar-slim -my-2 flex items-center gap-2 overflow-x-auto overscroll-x-contain py-2',
        'lg:my-0 lg:flex-wrap lg:gap-1 lg:overflow-x-visible lg:py-0',
        variant === 'subtab' && 'lg:gap-2',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <Chip
            key={option.value}
            ref={(element) => {
              register(option.value)(element)
              if (selected) activeRef.current = element
            }}
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            active={selected}
            variant={variant}
            {...(option.dot ? { dot: option.dot } : {})}
            onClick={() => {
              onChange(option.value)
            }}
          >
            {option.label}
          </Chip>
        )
      })}
    </div>
  )
}
